import type { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, MapPin, ShieldAlert } from 'lucide-react';

import { useAuth } from '@/contexts/auth.context';
import { toast } from '@/hooks/use-toast';
import {
  getSubscriberProfileCompletion,
  mapProfileToSubscriberProfileCompletionForm,
  subscriberProfileMissingFieldLabels,
  type SubscriberProfileCompletionFormData,
  type SubscriberProfileRequiredField,
} from '@/lib/subscriber-profile-completion';
import userService, { type ProfileResponse } from '@/services/user.service';
import { Button } from '@/components/ui/button';
import { CepInput } from '@/components/ui/cep-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type FieldErrors = Partial<Record<SubscriberProfileRequiredField, string>>;

/**
 * Por que o cadastro está sendo pedido: ao entrar no app (plano pago ativo sem
 * CPF/endereço) ou ao assinar um plano pago. Muda só o texto — em ambos o X
 * fecha (chamado 7116): ao entrar, a janela volta no próximo acesso; ao
 * assinar, fechar é desistir da assinatura.
 */
export type ProfileCompletionReason = 'access' | 'subscribe';

const TEXTOS: Record<ProfileCompletionReason, { intro: string; aviso: string }> = {
  access: {
    intro: 'Para continuar usando o Librattum, precisamos completar seus dados cadastrais.',
    aviso:
      'Você pode fechar agora, mas esta janela voltará no próximo acesso até que o cadastro seja concluído.',
  },
  subscribe: {
    intro: 'Para assinar o plano, precisamos completar seus dados cadastrais.',
    aviso: 'Esses dados são necessários para emitir a cobrança do plano.',
  },
};

interface SubscriberProfileCompletionModalProps {
  open: boolean;
  user: ProfileResponse | null;
  reason: ProfileCompletionReason;
  /** X ou Esc: fecha sem salvar. */
  onClose: () => void;
  /** Chamado após salvar os dados com sucesso (ex.: seguir para o checkout do plano pago). */
  onCompleted?: () => void;
}

export function SubscriberProfileCompletionModal({
  open,
  user,
  reason,
  onClose,
  onCompleted,
}: Readonly<SubscriberProfileCompletionModalProps>) {
  const { refreshProfile } = useAuth();
  const [formData, setFormData] = useState<SubscriberProfileCompletionFormData>(
    () => mapProfileToSubscriberProfileCompletionForm(user),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const completionState = useMemo(() => getSubscriberProfileCompletion(user), [user]);
  const missingFieldLabels = completionState.missingFields.map(
    (field) => subscriberProfileMissingFieldLabels[field],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormData(mapProfileToSubscriberProfileCompletionForm(user));
    setErrors({});
  }, [open, user]);

  if (!user) {
    return null;
  }

  const updateField = (field: keyof SubscriberProfileCompletionFormData, value: string) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
    if (field in errors) {
      setErrors((current) => ({
        ...current,
        [field as SubscriberProfileRequiredField]: undefined,
      }));
    }
  };

  const validate = () => {
    const nextErrors: FieldErrors = {};

    if (!formData.cpf.trim() && !formData.cnpj.trim()) {
      nextErrors.document = 'Informe um CPF ou CNPJ para continuar.';
    }
    if (!formData.cep.trim()) nextErrors.cep = 'Informe o CEP.';
    if (!formData.street.trim()) nextErrors.street = 'Informe o logradouro.';
    if (!formData.number.trim()) nextErrors.number = 'Informe o número.';
    if (!formData.neighborhood.trim()) nextErrors.neighborhood = 'Informe o bairro.';
    if (!formData.city.trim()) nextErrors.city = 'Informe a cidade.';
    if (!formData.state.trim()) nextErrors.state = 'Informe o estado.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: 'Dados incompletos',
        description: 'Preencha os campos obrigatórios para continuar.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      await userService.updateMyProfile({
        document: formData.cpf || undefined,
        cnpj: formData.cnpj || undefined,
        company_document: formData.cnpj || undefined,
        company_name: formData.companyName || undefined,
        cep: formData.cep || undefined,
        street: formData.street || undefined,
        number: formData.number || undefined,
        complement: formData.complement || undefined,
        neighborhood: formData.neighborhood || undefined,
        city: formData.city || undefined,
        state: formData.state.toUpperCase() || undefined,
      });

      await userService.syncMySubscriberProfileWithVio();
      await refreshProfile();

      toast({
        title: 'Cadastro atualizado',
        description: 'Seus dados foram salvos com sucesso!',
      });

      onCompleted?.();
    } catch (error: unknown) {
      const message =
        (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
        'Não foi possível salvar seus dados cadastrais.';
      toast({
        title: 'Erro ao atualizar cadastro',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(aberto) => {
        if (!aberto) onClose();
      }}
    >
      {/* Clique fora não fecha: perderia o que já foi digitado. X e Esc fecham. */}
      <DialogContent
        className="sm:max-w-2xl"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            Complete seu cadastro para continuar
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">{TEXTOS[reason].intro}</span>
            {missingFieldLabels.length > 0 && (
              <span className="block text-foreground">
                Pendências identificadas: <strong>{missingFieldLabels.join(', ')}</strong>.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {TEXTOS[reason].aviso}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="subscriber-cpf">CPF</Label>
              <Input
                id="subscriber-cpf"
                value={formData.cpf}
                onChange={(event) => updateField('cpf', event.target.value)}
                placeholder="000.000.000-00"
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subscriber-cnpj">CNPJ</Label>
              <Input
                id="subscriber-cnpj"
                value={formData.cnpj}
                onChange={(event) => updateField('cnpj', event.target.value)}
                placeholder="00.000.000/0000-00"
                disabled={isSaving}
              />
            </div>
          </div>

          {errors.document && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{errors.document}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="subscriber-company-name">Razão social ou nome da empresa</Label>
            <Input
              id="subscriber-company-name"
              value={formData.companyName}
              onChange={(event) => updateField('companyName', event.target.value)}
              placeholder="Opcional"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              Endereço de cadastro
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <CepInput
                value={formData.cep}
                onChange={(value) => updateField('cep', value)}
                onAddressFound={(address) => {
                  setFormData((current) => ({
                    ...current,
                    cep: current.cep,
                    street: current.street || address.street || '',
                    neighborhood: current.neighborhood || address.neighborhood || '',
                    city: current.city || address.city || '',
                    state: current.state || address.state || '',
                  }));
                }}
                disabled={isSaving}
                required
                error={errors.cep}
                className="md:col-span-1"
              />

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="subscriber-street">Logradouro</Label>
                <Input
                  id="subscriber-street"
                  value={formData.street}
                  onChange={(event) => updateField('street', event.target.value)}
                  disabled={isSaving}
                />
                {errors.street && <p className="text-xs text-destructive">{errors.street}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriber-number">Número</Label>
                <Input
                  id="subscriber-number"
                  value={formData.number}
                  onChange={(event) => updateField('number', event.target.value)}
                  disabled={isSaving}
                />
                {errors.number && <p className="text-xs text-destructive">{errors.number}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriber-complement">Complemento</Label>
                <Input
                  id="subscriber-complement"
                  value={formData.complement}
                  onChange={(event) => updateField('complement', event.target.value)}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriber-neighborhood">Bairro</Label>
                <Input
                  id="subscriber-neighborhood"
                  value={formData.neighborhood}
                  onChange={(event) => updateField('neighborhood', event.target.value)}
                  disabled={isSaving}
                />
                {errors.neighborhood && <p className="text-xs text-destructive">{errors.neighborhood}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriber-city">Cidade</Label>
                <Input
                  id="subscriber-city"
                  value={formData.city}
                  onChange={(event) => updateField('city', event.target.value)}
                  disabled={isSaving}
                />
                {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriber-state">Estado</Label>
                <Input
                  id="subscriber-state"
                  value={formData.state}
                  onChange={(event) => updateField('state', event.target.value.toUpperCase().slice(0, 2))}
                  disabled={isSaving}
                  maxLength={2}
                />
                {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SubscriberProfileCompletionModal;
